import { Edit01Icon } from '@langchain/untitled-ui-icons';
import { FormControl, Switch } from '@mui/joy';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useUpdateOAuthProviderMutation } from '@/Pages/Settings/hooks/useOAuthProviders';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Button, Input, Text } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';
import { OAuthProviderSchema, UpdateOAuthProviderBody } from '@/types/schema';

type EditOAuthProviderFormData = {
  provider_id: string;
  name: string;
  client_id: string;
  client_secret: string;
  auth_url: string;
  token_url: string;
  uses_pkce: boolean;
  code_challenge_method: string;
};

function EditOAuthProviderForm(props: {
  provider: OAuthProviderSchema;
  onSubmit: (data: EditOAuthProviderFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  error: Error | null;
}) {
  'use no memo';
  const form = useForm<EditOAuthProviderFormData>({
    defaultValues: {
      provider_id: props.provider.provider_id,
      name: props.provider.name,
      client_id: props.provider.client_id,
      client_secret: '', // Don't pre-populate for security
      auth_url: props.provider.auth_url,
      token_url: props.provider.token_url,
      uses_pkce: props.provider.uses_pkce,
      code_challenge_method: props.provider.code_challenge_method || 'S256',
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit(props.onSubmit)}
      className="flex flex-col gap-4"
    >
      <FormControl disabled>
        <Text variant="sm" weight="medium" as="label">
          Provider ID
        </Text>
        <Controller
          name="provider_id"
          control={form.control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Provider ID cannot be changed"
              size="md"
              disabled
            />
          )}
        />
      </FormControl>

      <FormControl error={!!form.formState.errors.name}>
        <Text variant="sm" weight="medium" as="label">
          Display Name
        </Text>
        <Controller
          name="name"
          control={form.control}
          rules={{ required: 'Display name is required' }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="e.g., GitHub OAuth, Google Workspace"
              size="md"
            />
          )}
        />
        {form.formState.errors.name && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.name.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.client_id}>
        <Text variant="sm" weight="medium" as="label">
          Client ID
        </Text>
        <Controller
          name="client_id"
          control={form.control}
          rules={{ required: 'Client ID is required' }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="OAuth application client ID"
              size="md"
            />
          )}
        />
        {form.formState.errors.client_id && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.client_id.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.client_secret}>
        <Text variant="sm" weight="medium" as="label">
          Client Secret (leave empty to keep current)
        </Text>
        <Controller
          name="client_secret"
          control={form.control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              placeholder="Enter new client secret or leave empty"
              size="md"
            />
          )}
        />
        {form.formState.errors.client_secret && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.client_secret.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.auth_url}>
        <Text variant="sm" weight="medium" as="label">
          Authorization URL
        </Text>
        <Controller
          name="auth_url"
          control={form.control}
          rules={{
            required: 'Authorization URL is required',
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Must be a valid URL',
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="https://example.com/oauth/authorize"
              size="md"
            />
          )}
        />
        {form.formState.errors.auth_url && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.auth_url.message}
          </Text>
        )}
      </FormControl>

      <FormControl error={!!form.formState.errors.token_url}>
        <Text variant="sm" weight="medium" as="label">
          Token URL
        </Text>
        <Controller
          name="token_url"
          control={form.control}
          rules={{
            required: 'Token URL is required',
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Must be a valid URL',
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="https://example.com/oauth/token"
              size="md"
            />
          )}
        />
        {form.formState.errors.token_url && (
          <Text variant="xs" className="mt-1 text-red-500">
            {form.formState.errors.token_url.message}
          </Text>
        )}
      </FormControl>

      <div className="flex items-center justify-between">
        <Text variant="sm" weight="medium">
          Enable PKCE
        </Text>
        <Controller
          name="uses_pkce"
          control={form.control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(event) => field.onChange(event.target.checked)}
              sx={{
                '--Switch-thumbSize': '16px',
                '--Switch-trackWidth': '36px',
                '--Switch-trackHeight': '20px',
              }}
            />
          )}
        />
      </div>

      {props.error && <ExpandableErrorAlert error={props.error} />}

      <div className="flex justify-end gap-3">
        <Button
          variant="outlined"
          size="md"
          onClick={props.onCancel}
          disabled={form.formState.isSubmitting || props.isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="md"
          loading={form.formState.isSubmitting || props.isLoading}
          disabled={!form.watch('name')?.trim()}
        >
          Update OAuth Provider
        </Button>
      </div>
    </form>
  );
}

export function EditOAuthProviderModal(props: {
  provider: OAuthProviderSchema;
  className?: string;
  onSuccess?: () => void;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { authorize } = usePermissions();
  const updateProviderMutation = useUpdateOAuthProviderMutation(
    props.provider.provider_id,
    {
      onSuccess: () => {
        props.onSuccess?.();
        setOpen(false);
      },
    }
  );

  const handleSubmit = (data: EditOAuthProviderFormData) => {
    if (!authorize('workspaces:manage')) {
      return;
    }

    const updateData: UpdateOAuthProviderBody = {
      name: data.name,
      client_id: data.client_id,
      auth_url: data.auth_url,
      token_url: data.token_url,
      uses_pkce: data.uses_pkce,
      code_challenge_method: data.code_challenge_method,
    };

    // Only include client_secret if it's not empty
    if (data.client_secret && data.client_secret.trim()) {
      updateData.client_secret = data.client_secret;
    }

    updateProviderMutation.trigger({ json: updateData });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Edit OAuth Provider"
          className="max-w-2xl border-b-0 [&>*]:border-b-0 [&_.dialog-header]:border-b-0 [&_[data-dialog-title]]:text-xl [&_[data-dialog-title]]:font-semibold"
        >
          <div className="flex flex-col gap-4">
            {updateProviderMutation.error && (
              <ExpandableErrorAlert error={updateProviderMutation.error} />
            )}
            <EditOAuthProviderForm
              provider={props.provider}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={updateProviderMutation.isMutating}
              error={null}
            />
          </div>
        </DialogContent>
      </Dialog>
      {props.children ? (
        <div onClick={() => setOpen(true)}>{props.children}</div>
      ) : (
        <Button
          variant="outlined"
          size="sm"
          onClick={() => setOpen(true)}
          className={props.className}
        >
          <Edit01Icon className="h-4 w-4" />
        </Button>
      )}
    </>
  );
}
